import moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import BottomPagination from '../../../components/Pagination/BottomPagination/BottomPagination';
import TopPagination from '../../../components/Pagination/TopPagination/TopPagination';
import { HeaderContainer, PageTitle } from '../../../styledComponents/common';
import ApprovalSubTaskList from './ApprovalSubTaskList';
import SubTaskFilter from './SubTaskFilter';

const SubTaskApproval = ({
  selectedOrganization,
  getOrganizationMembersList,
  orgMembersList,
  loadingTaskAssigneeId,
  isLoading,
  officesShortList,
  getOfficesShortList,
  getItemSubTasks,
  updateSubTaskApproval,
  itemSubTasksList,
  totalSubTasksCount,
  pageSize,
  history,
  getSubTaskEvidence,
  locationsList,
  getTaskLocation,
  getPublicZoneList,
  publicZoneList,
}) => {
  const { t } = useTranslation();
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromParams = urlParams.get('tab');
  if (tabFromParams) {
    const newUrl = `${window.location.pathname}`;
    window.history.replaceState({}, '', newUrl);
  }

  const [selectedMember, setSelectedMember] = useState('');
  const [selectedPublicZone, setSelectedPublicZone] = useState('');
  const [selectedDate, setSelectedDate] = useState({
    startDate: tabFromParams
      ? moment('2024-04-01')
      : moment().subtract(7, 'days'),
    endDate: moment(new Date()),
  });
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTab, setSelectedTab] = useState('submitted');

  // pagination
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentSearchedTerm, setCurrentSearchedTerm] = useState('');
  const [expandedSubTaskId, setExpandedSubTaskId] = useState(null);

  const onPublicZoneChange = e => {
    setSelectedPublicZone(e);
  };

  useEffect(() => {
    setSelectedOffice({
      label: t('all_offices'),
      value: null,
    });
    getTaskLocation();
    getPublicZoneList();
  }, []);

  const selectLocation = e => {
    setCurrentPageNumber(1);
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setSelectedLocation(e);
  };

  const handleSearchByName = () => {
    if (searchTerm && searchTerm.trim()) {
      setCurrentSearchedTerm(searchTerm);
      setCurrentPageNumber(1);
    }
  };

  const handleClearSearch = () => {
    setCurrentPageNumber(1);
    setSearchTerm('');
    setCurrentSearchedTerm('');
  };

  const handlePageChange = page => {
    setCurrentPageNumber(page);
  };

  const selectOffice = e => {
    setCurrentPageNumber(1);
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setSelectedOffice(e);
  };

  const selectMember = person => {
    if (person.id) {
      setSelectedMember(person);
      setSearchTerm('');
      setCurrentSearchedTerm('');
      setCurrentPageNumber(1);
    }
  };

  const clearMember = () => {
    setSearchTerm('');
    setCurrentSearchedTerm('');
    setCurrentPageNumber(1);
    setSelectedMember('');
  };

  const onSearchTermChange = e => {
    const { value } = e.target;
    if (value !== '') {
      setSelectedMember('');
    }
    setSearchTerm(value);
  };

  const handleDateChange = date => {
    setCurrentPageNumber(1);
    setSelectedDate(date);
  };

  const handleSelectedTab = selectedTab => {
    setCurrentPageNumber(1);
    setSelectedTab(selectedTab);
  };

  const updateSubTaskStatusRequest = (sub_task_id, approval_status) => {
    const data = approval_status;
    updateSubTaskApproval({
      sub_task_id,
      data,
    });
  };

  const getSubTasks = () => {
    const payload = {
      startDate: moment(selectedDate.startDate).format('YYYY-MM-DD'),
      endDate: moment(selectedDate.endDate).format('YYYY-MM-DD'),
      page: currentPageNumber,
      approval_status: selectedTab === 'submitted' ? 'pending' : selectedTab,
    };

    if (selectedOffice && selectedOffice.value) {
      payload.office_id = selectedOffice.value;
    }

    if (selectedPublicZone && selectedPublicZone.value) {
      payload.public_zone_id = selectedPublicZone.value;
    }

    if (selectedLocation && selectedLocation.value) {
      payload.location_id = selectedLocation.value;
    }

    if (selectedMember && selectedMember.id) {
      payload.assignee_id = selectedMember.id;
    }

    if (searchTerm && searchTerm.trim()) {
      payload.task_name = searchTerm.toLowerCase().trim();
    }

    // Remove null or undefined properties
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v != null),
    );

    getItemSubTasks(filteredPayload);
  };

  useEffect(() => {
    getSubTasks();
  }, [
    currentPageNumber,
    selectedDate,
    selectedOffice,
    selectedMember,
    selectedLocation,
    selectedTab,
    currentSearchedTerm,
    selectedPublicZone,
  ]);

  useEffect(() => {
    if (tabFromParams) {
      handleSelectedTab(tabFromParams);
    }
  }, []);

  return (
    <div className="content">
      <PageTitle>{t('submitted_sub_tasks_for_approval')}</PageTitle>
      <HeaderContainer>
        <SubTaskFilter
          selectedOrganization={selectedOrganization}
          getOrganizationMembersList={getOrganizationMembersList}
          organizationMembersList={orgMembersList}
          officesShortList={officesShortList}
          getOfficesShortList={getOfficesShortList}
          selectedOffice={selectedOffice}
          selectOffice={selectOffice}
          locationList={locationsList}
          selectedLocation={selectedLocation}
          selectLocation={selectLocation}
          selectedMember={selectedMember}
          selectMember={selectMember}
          clearMember={clearMember}
          searchTerm={searchTerm}
          onSearchTermChange={onSearchTermChange}
          selectedDate={selectedDate}
          handleDateChange={handleDateChange}
          handleSearchByName={handleSearchByName}
          handleClearSearch={handleClearSearch}
          handleSelectedTab={handleSelectedTab}
          selectedTab={selectedTab}
          selectedPublicZone={selectedPublicZone}
          onPublicZoneChange={onPublicZoneChange}
          publicZoneList={publicZoneList}
        />
      </HeaderContainer>
      {totalSubTasksCount && totalSubTasksCount > 0 && pageSize ? (
        <TopPagination
          itemName="sub tasks"
          totalCount={totalSubTasksCount}
          currentPage={currentPageNumber}
          onPageChange={page => handlePageChange(page)}
          pageSize={pageSize}
        />
      ) : null}
      <ApprovalSubTaskList
        selectedOrganization={selectedOrganization}
        loadingTaskAssigneeId={loadingTaskAssigneeId}
        expandedSubTaskId={expandedSubTaskId}
        setExpandedSubTaskId={setExpandedSubTaskId}
        history={history}
        subTasksList={itemSubTasksList}
        isLoading={isLoading}
        handlePageChange={handlePageChange}
        updateSubTaskStatusRequest={updateSubTaskStatusRequest}
        selectedTab={selectedTab}
        getSubTaskEvidence={getSubTaskEvidence}
      />
      {totalSubTasksCount && totalSubTasksCount > 0 && pageSize ? (
        <BottomPagination
          totalCount={totalSubTasksCount}
          currentPage={currentPageNumber}
          onPageChange={page => handlePageChange(page)}
          pageSize={pageSize}
        />
      ) : null}
    </div>
  );
};

export default SubTaskApproval;
